<template>
    <main class="relative h-full font-roboto overflow-hidden">
        <img src="../../assets/images/login_bg.jpg" alt=""
            class="w-full h-screen object-cover mobile:scale-[1.5] tablet:scale-[1.4] laptop:scale-100">
        <div class="absolute mobile:w-full largeDesktop:w-[700px] laptop:w-[550px] top-0 right-0">
            <div
                class="mobile:px-8 tablet:px-32 laptop:px-48 desktop:px-16 largeDesktop:px-28 h-screen flex flex-col items-center justify-center bg-[#2359a8f0] laptop:bg-[#2459A8]">
                <img src="../../assets/images/MyHappy-Logo.png"
                    class="w-[210px] tablet:w-[265px] object-cover mb-[20px] largeDesktop:mb-[70px]" alt="fyxify Logo">

                <!-- Step 1: Email Input -->
                <div v-if="step === 1">
                    <h4
                        class="text-center font-roboto text-2xl tablet:text-3xl largeDesktop:text-[40px] font-medium mobile:my-3 tablet:my-5 largeDesktop:my-[30px] text-white">
                        Forgot Password?
                    </h4>
                    <p
                        class="mb-4 leading-[1.2] text-center font-roboto mobile:text-[16px] desktop:text-[20px] largeDesktop:text-[22px] font-normal text-white">
                        Enter your email address below and we'll send you instructions on how to change your password!
                    </p>
                    <div
                        class="grid w-full tablet:max-w-[450px] mobile:gap-2 tablet:gap-4 largeDesktop:gap-5 mt-[20px] laptop:mt-[30px] largeDesktop:mt-[50px] mx-auto">
                        <div class="form-group">
                            <label for="email" class="text-base font-roboto mb-1 text-white">Email:</label>
                            <inputComponent ref="emailRef" v-model="email" name="email" required
                                errorClass="!text-customYellow-100" :regex="$constants.EMAIL_REGEX"
                                error-message="Please enter a valid email address" placeholder="Enter Email"
                                customClass="!text-[16px] mobile:!p-[9px] largeDesktop:!p-[11px] !rounded-[5px]" />
                        </div>
                    </div>
                    <div class="flex justify-center gap-3 mt-[30px] laptop:mt-[40px] largeDesktop:mt-[50px]">
                        <button
                            class="CustomYellowBtn px-10 py-2 font-roboto font-bold text-base tablet:text-lg uppercase rounded-full border-[1px] border-customYellow-100 lg:text-customYellow-100 lg:!bg-transparent lg:!shadow-none focus:outline-none"
                            @click="backClicked">Cancel</button>
                        <button
                            class="CustomYellowBtn px-10 py-2 font-roboto font-bold text-base tablet:text-lg uppercase rounded-full"
                            @click="goToStep(2)">Submit</button>
                    </div>
                </div>

                <!-- Step 2: OTP Verification -->
                <div v-else-if="step === 2">
                    <h2
                        class="text-center font-roboto text-2xl tablet:text-3xl largeDesktop:text-[40px] font-medium mobile:my-3 tablet:my-[5px] largeDesktop:my-[30px] text-white">
                        Reset Password</h2>
                    <p
                        class="mb-4 leading-[1.2] text-center font-roboto mobile:text-[16px] desktop:text-[20px] largeDesktop:text-[22px] font-normal text-white">
                        A One Time Pass Code has been sent to your email {{ email }}</p>
                    <div
                        class="grid w-full tablet:max-w-[450px] mobile:gap-2 tablet:gap-4 largeDesktop:gap-5 mt-[15px] largeDesktop:mt-[50px] mx-auto">
                        <div class="form-group">
                            <label for="email" class="text-base font-roboto mb-1 text-white">Code:</label>
                            <inputComponent type="number" ref="codeRef" v-model="otp"
                                placeholder="Enter Code" name="otp" required
                                errorClass="!text-customYellow-100" :regex="/^.{6}$/"
                                error-message="Please enter valid code" :length="6"
                                customClass="!text-[16px] mobile:!p-[9px] largeDesktop:!p-[11px] !rounded-[5px]" />
                        </div>
                    </div>
                    <div>
                        <div class="flex justify-center gap-3 mt-[20px] largeDesktop:mt-[50px]">
                            <button
                                class="CustomYellowBtn px-10 py-2 font-roboto font-bold text-base tablet:text-lg uppercase rounded-full border-[1px] border-customYellow-100 lg:text-customYellow-100 lg:!bg-transparent lg:!shadow-none focus:outline-none"
                                @click="backClicked">Cancel</button>
                            <button
                                class="CustomYellowBtn px-10 py-2 font-roboto font-bold text-base tablet:text-lg uppercase rounded-full "
                                @click="goToStep(3)">Verify</button>
                        </div>
                        <div class="mb-4 mt-[10px] laptop:mt-[20px] largeDesktop:mt-[60px] text-base text-white">
                            <p v-if="timer > 0" class="m-0 text-center text-[20px]">
                                Didn’t receive the code? You’ll be able to resend it in
                                <span>{{ timer == 0 ? '' : timer + ' seconds' }}</span>
                            </p>
                        </div>
                        <div class="flex item-center justify-center gap-3">
                            <button @click="resendCode" :disabled="timer > 0"
                                class="CustomYellowBtn w-max py-2 px-[30px] rounded-lg text-base tablet:text-lg disabled:border-[#ffffff7a] disabled:bg-transparent disabled:text-[#ffffff7a] disabled:cursor-not-allowed disabled:!shadow-none border-[1px] border-customYellow-100 lg:text-customYellow-100 lg:!bg-transparent lg:!shadow-none focus:outline-none">Re-send</button>
                        </div>
                    </div>
                </div>

                <!-- Step 3: Set New Password -->
                <div class="w-full" v-else-if="step === 3">
                    <h2
                        class="text-center font-roboto text-2xl tablet:text-3xl largeDesktop:text-[40px] font-medium mobile:my-3 tablet:my-[15px] largeDesktop:my-[30px] text-white">
                        Reset Password</h2>
                    <div
                        class="grid w-full tablet:max-w-[450px] mobile:gap-[15px] tablet:gap-4 largeDesktop:gap-5 mt-[15px] largeDesktop:mt-[50px] mx-auto">
                        <div class="form-group">
                            <label for="password" class="text-base font-roboto  mb-1 text-white">New Password:</label>
                            <div class="relative">
                                <PasswordInput v-model="password" ref="passwordRef" name="password"
                                    required :regex="$constants.PASSWORD_REGEX" errorClass="!text-customYellow-100"
                                    error-message="Minimum 8 characters with uppercase, number & symbol. Emojis and spaces are not allowed."
                                    placeholder="Enter Password"
                                    customClass="!text-[16px] mobile:!py-[9px] mobile:!pl-[9px] largeDesktop:!py-[11px] largeDesktop:!pl-[11px] !rounded-[5px] mobile:!pr-[30px] largeDesktop:!pr-[35px]" />
                            </div>
                        </div>
                        <div class="form-group">
                            <label for="password" class="text-base font-roboto  mb-1 text-white">Confirm
                                Password:</label>
                            <div class="relative">
                                <inputComponent v-model="confirmPassword" ref="confirmPasswordRef"
                                    name="confirmPassword" type="password" required :confirmWith="password"
                                    errorClass="!text-customYellow-100" placeholder="Enter Confirm Password"
                                    error-message="Passwords do not match"
                                    customClass="!text-[16px] mobile:!p-[9px] largeDesktop:!p-[11px] !rounded-[5px]" />
                            </div>
                        </div>
                    </div>
                    <div class="flex justify-center gap-3 mt-[20px] largeDesktop:mt-[70px]">
                        <button
                            class="CustomYellowBtn px-10 py-2 font-roboto font-bold text-base tablet:text-lg uppercase rounded-full border-[1px] border-customYellow-100 lg:text-customYellow-100 lg:!bg-transparent lg:!shadow-none focus:outline-none"
                            @click="backClicked">Cancel</button>
                        <button
                            class="CustomYellowBtn px-10 py-2 font-roboto font-bold text-base tablet:text-lg uppercase rounded-full"
                            @click="submitNewPassword">Submit</button>
                    </div>
                </div>

                <!-- Step Indicator -->
                <div class="mt-[20px] laptop:mt-[30px] largeDesktop:mt-[40px] flex justify-center space-x-2">
                    <span v-for="n in 3" :key="n" class="h-2 w-2 rounded-full"
                        :class="step === n ? 'w-8 bg-customYellow-100' : 'bg-white'"></span>
                </div>
            </div>
        </div>
    </main>
</template>

<script>
import inputComponent from '../../components/partialComponent/inputComponent.vue';
import PasswordInput from '../../components/partialComponent/PasswordInput.vue';
import { useToast } from "vue-toastification";
import { mapActions, mapGetters } from "vuex";
const toast = useToast();
export default {
    components: {
        inputComponent,
        PasswordInput
    },
    data() {
        return {
            step: 1,
            email: '',
            otp: '',
            password: '',
            confirmPassword: '',
            showPassword: false,
            timer: 30,
            interval: null,
        }
    },
    computed: {
        ...mapGetters([
            "resetAuthResponse",
            "resetResendCodeResponse",
            "resetVerifyResponse",
            "resetPasswordResponse"
        ]),
    },

    watch: {
        resetAuthResponse: function (data) {
            toast.success(this.resetAuthResponse.message);
            this.timer = 30;
            this.startTimer();
            this.step = 2
        },
        resetResendCodeResponse: function (data) {
            toast.success(this.resetResendCodeResponse.message);
            this.timer = 30;
            this.startTimer();
        },
        resetVerifyResponse: function (data) {
            this.step = 3
        },
        resetPasswordResponse: function (data) {
            toast.success(this.resetPasswordResponse.message);
            this.$router.go(-1)
        }
    },

    mounted() {
        if (this.step == '2') {
            this.startTimer();
        }

    },
    unmounted() {
        if (this.interval) clearInterval(this.interval);
    },

    methods: {
        ...mapActions([
            "resetAuth",
            "resetResendCode",
            "resetVerify",
            "resetPassword"
        ]),

        blockAlphabet(e) {
            // Allow deletion
            if (e.inputType?.includes('delete')) return

            // Block anything that's not a number
            if (!/^\d+$/.test(e.data)) {
                e.preventDefault()
            }
        },

        goToStep(step) {
            if (step == "2") {
                this.authenticateClicked()
            } else if (step == "3") {
                this.verifyClicked()
            } else {
                this.step = step
            }
        },

        backClicked() {
            this.$router.go(-1)
        },
        authenticateClicked() {
            let ref = this.$refs.emailRef
            if (ref?.validate?.()) {
                return
            }
            this.resetAuth({
                email: this.email.trim()
            })
        },
        verifyClicked() {
            let ref = this.$refs.codeRef
            if (ref?.validate?.()) {
                return
            }
            this.resetVerify({
                email: this.email.trim(),
                code: this.otp
            })
        },
        resendCode() {
            this.resetResendCode({
                email: this.email.trim()
            })
        },
        submitNewPassword() {
            let passwordRef = this.$refs.passwordRef
            let confirmPasswordRef = this.$refs.confirmPasswordRef

            if (passwordRef?.validate?.() || confirmPasswordRef?.validate?.()) {
                return
            }

            this.resetPassword({
                password: this.password,
                password_confirmation: this.confirmPassword,
                temp_token: this.resetVerifyResponse.temp_token
            })
        },
        startTimer() {
            if (this.interval) clearInterval(this.interval);
            this.interval = setInterval(() => {
                if (this.timer > 0) {
                    this.timer--;
                } else {
                    clearInterval(this.interval);
                }
            }, 1000);
        },
    }

};
</script>
